import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
  Table,
} from 'reactstrap';
import classnames from 'classnames';
import { motion } from 'framer-motion';
import { Activity, ClipboardList, Download, FileText, LayoutDashboard, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatDateTimeForAPI } from '../../constants/utils';
import {
  fetchGlobalAgingBuckets,
  fetchGlobalAssetMtbf,
  fetchGlobalByTypeList,
  fetchGlobalCompletedByUser,
  fetchGlobalCompletedByUserList,
  fetchGlobalCreatedList,
  fetchGlobalMttrList,
  fetchGlobalOnTimeOverdueList,
  fetchGlobalPriorityList,
  fetchGlobalRepeatingList,
  fetchGlobalResolvedList,
  fetchGlobalStatusList,
  fetchGlobalTimeToCompleteList,
  fetchGlobalCurrentStatus,
  fetchGlobalDashboardKpis,
  fetchGlobalFilterAssetCategories,
  fetchGlobalFilterAssets,
  fetchGlobalFilterContracts,
  fetchGlobalFilterCriticality,
  fetchGlobalFilterLocations,
  fetchGlobalFilterTechnicians,
  fetchGlobalFilterVendors,
  fetchGlobalFilterWorkOrderCategories,
  fetchGlobalDowntimeBreakdown,
  fetchGlobalDowntimeCost,
  fetchGlobalMostProblematic,
  fetchGlobalOnTimeOverdue,
  fetchGlobalPerformance,
  fetchGlobalRepeatingReport,
  fetchGlobalStatusReport,
  fetchGlobalSubOrganizations,
  fetchGlobalTimeToComplete,
  fetchGlobalTopOverdue,
  fetchGlobalTotalDowntime,
  fetchGlobalWoBreakdown,
  fetchGlobalWoTrend,
} from '../../api/global';
import {
  refreshAggregateViewOrgs,
  switchContext,
} from '../../store/activeContext/actions';
import DateRangeSelector from '../Reporting/DateRangeSelector';
import ReportingFilterBar from '../Reporting/components/ReportingFilterBar';
import ReportWidget from '../Reporting/components/ReportWidget';
import CreatedVsResolvedWidget from '../Reporting/CreatedVsResolvedWidget';
import WorkOrdersByTypeWidget from '../Reporting/WorkOrdersByTypeWidget';
import RepeatingReportWidget from '../Reporting/RepeatingReportWidget';
import PriorityReportWidget from '../Reporting/PriorityReportWidget';
import StatusReportWidget from '../Reporting/StatusReportWidget';
import OnTimeOverdueWidget from '../Reporting/OnTimeOverdueWidget';
import TimeToCompleteWidget from '../Reporting/TimeToCompleteWidget';
import CompletedByUserWidget from '../Reporting/CompletedByUserWidget';
import CurrentStatusWidget from '../Reporting/CurrentStatusWidget';
import MostProblematicWidget from '../Reporting/MostProblematicWidget';
import DowntimeBreakdownWidget from '../Reporting/DowntimeBreakdownWidget';
import TotalDowntimeWidget from '../Reporting/TotalDowntimeWidget';
import DowntimeCostWidget from '../Reporting/DowntimeCostWidget';
import MtbfWidget from '../Reporting/MtbfWidget';
import SwitchContextConfirmModal from '../../components/Common/SwitchContextConfirmModal';
import WorkOrderListModal from '../Reporting/WorkOrderListModal';
import ReportingDetailsTab from '../Reporting/ReportingDetailsTab';
import ExportDataTab from '../Reporting/ExportDataTab';
import SectionHeading from './components/overview/SectionHeading';
import AssetStatusCard from './components/overview/AssetStatusCard';
import AvailabilityCard from './components/overview/AvailabilityCard';
import MtbfMttrCard from './components/overview/MtbfMttrCard';
import WoBreakdownCard, {
  CompletedPctRow,
  SegmentedBarRow,
  ROW_COLORS,
} from './components/overview/WoBreakdownCard';
import OrganizationsTable from './components/overview/OrganizationsTable';
import { percentDelta, pct as safePct } from './components/overview/helpers';

const sum = (arr, fn) => arr.reduce((a, r) => a + (fn(r) || 0), 0);

const GlobalDashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  document.title = `${t('shared.reporting', 'Reporting')} | Arcmedix`;

  const [kpis, setKpis] = useState(null);
  const [trend, setTrend] = useState(null);
  const [topOverdue, setTopOverdue] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [woBreakdown, setWoBreakdown] = useState(null);
  const [agingBuckets, setAgingBuckets] = useState([]);
  const [statusReport, setStatusReport] = useState(null);
  const [onTimeOverdue, setOnTimeOverdue] = useState(null);
  const [timeToComplete, setTimeToComplete] = useState(null);
  const [repeatingReport, setRepeatingReport] = useState(null);
  const [completedByUser, setCompletedByUser] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [mostProblematic, setMostProblematic] = useState({ totalAssets: 0, assets: [] });
  const [downtimeBreakdown, setDowntimeBreakdown] = useState({ totalAssets: 0, assets: [] });
  const [totalDowntime, setTotalDowntime] = useState(null);
  const [downtimeCost, setDowntimeCost] = useState(null);
  const [assetMtbf, setAssetMtbf] = useState(null);
  const [subOrgs, setSubOrgs] = useState([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState([]); // [] = all
  // Prior-window state — drives the delta arrows on the Overview cards.
  // Same shape as their current-window counterparts.
  const [prevPerformance, setPrevPerformance] = useState(null);
  const [prevTrend, setPrevTrend] = useState(null);
  const [prevWoBreakdown, setPrevWoBreakdown] = useState(null);
  // Date range stored as Date objects so we share the per-org DateRangeSelector
  // contract exactly. Default = last 30 days, end-of-day inclusive.
  const [dateRange, setDateRange] = useState(() => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    return { startDate, endDate };
  });
  // YYYY-MM-DD strings for the backend; derived from the Date objects above.
  const dateRangeIso = useMemo(() => ({
    from: dateRange.startDate.toISOString().slice(0, 10),
    to: dateRange.endDate.toISOString().slice(0, 10),
  }), [dateRange.startDate, dateRange.endDate]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Default tab matches the per-org Reporting page (Work Orders first).
  const [activeTab, setActiveTab] = useState('1');
  // Pending sub-org jump for the confirmation modal — null when no modal.
  const [pendingSwitch, setPendingSwitch] = useState(null);
  // Drill-down modal state (Phase 3). One shared modal across every WO-tab
  // widget — the click handler chooses the right fetcher and pipes the
  // response in. Loading/error are local so the modal can show a spinner
  // independently of the page-level load() cycle.
  const [modalOpen, setModalOpen] = useState(false);
  const [workOrderList, setWorkOrderList] = useState(null);
  const [workOrderListLoading, setWorkOrderListLoading] = useState(false);
  const [workOrderListError, setWorkOrderListError] = useState(null);
  // Reporting Details tab (Phase 4). Tracks which report the dropdown is
  // showing; the title-chevron onDetailsClick on each WO-tab widget sets
  // this and jumps the user to tab "4". Default mirrors per-org.
  const [detailReportKey, setDetailReportKey] = useState('createdVsResolved');

  // =========================================================================
  // Per-tab filter state. Mirrors the per-org ReportingDashboard pattern so
  // we can mount the same ReportingFilterBar widget on each Global tab and
  // inherit its visual chrome + + Add Filter behavior. Each tab carries the
  // 12-field filter superset; only the chips its tab renders set values.
  // =========================================================================
  const PRIORITY_OPTIONS = useMemo(() => [
    { value: 'none', label: t('shared.none', 'None') },
    { value: 'low', label: t('shared.low', 'Low') },
    { value: 'medium', label: t('shared.medium', 'Medium') },
    { value: 'high', label: t('shared.high', 'High') },
  ], [t]);

  // Static status options — mirrors the per-org ReportingDashboard list verbatim
  // so the status filter chip renders the same labels and dispatches the same
  // string values for both surfaces.
  const STATUS_OPTIONS = useMemo(() => [
    { value: 'OPEN', label: t('shared.open', 'Open') },
    { value: 'IN_PROGRESS', label: t('shared.in_progress', 'In Progress') },
    { value: 'ON_HOLD', label: t('shared.on_hold', 'On Hold') },
    { value: 'COMPLETED', label: t('shared.completed', 'Completed') },
    { value: 'CANCELLED', label: t('shared.cancelled', 'Cancelled') },
  ], [t]);

  const emptyFilters = useMemo(() => ({
    criticality: null, locationId: null, vendorId: null, assetId: null, assetTypeId: null,
    contractNumber: null, workOrderCategoryId: null,
    assignedTo: null, priority: null, status: null,
    dueDateStart: null, dueDateEnd: null,
  }), []);
  const [tabFilters, setTabFilters] = useState({
    overview: { ...emptyFilters },
    workOrders: { ...emptyFilters },
    assetHealth: { ...emptyFilters },
  });
  const [visibleExtras, setVisibleExtras] = useState({
    overview: [],
    workOrders: [],
    assetHealth: [],
  });
  const updateTabFilters = useCallback((key, partial) => {
    setTabFilters(prev => ({ ...prev, [key]: { ...prev[key], ...partial } }));
  }, []);
  const addExtraFilter = useCallback((key, filterKey) => {
    setVisibleExtras(prev => prev[key].includes(filterKey)
      ? prev
      : { ...prev, [key]: [...prev[key], filterKey] });
  }, []);
  const removeExtraFilter = useCallback((key, filterKey) => {
    setVisibleExtras(prev => ({ ...prev, [key]: prev[key].filter(k => k !== filterKey) }));
  }, []);

  // Filter dropdown option lists. Loaded once from the global filter-options
  // endpoints (aggregated across all permitted sub-orgs).
  const [technicianOptions, setTechnicianOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [criticalityOptions, setCriticalityOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [assetTypeOptions, setAssetTypeOptions] = useState([]);
  const [contractOptions, setContractOptions] = useState([]);
  const [assetOptions, setAssetOptions] = useState([]);
  const [workOrderCategoryOptions, setWorkOrderCategoryOptions] = useState([]);
  const [refDataLoading, setRefDataLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRefDataLoading(true);
    Promise.all([
      fetchGlobalFilterTechnicians().then((r) => r.data?.response || []).catch(() => []),
      fetchGlobalFilterLocations().then((r) => r.data?.response || []).catch(() => []),
      fetchGlobalFilterCriticality().then((r) => r.data?.response || []).catch(() => []),
      fetchGlobalFilterVendors().then((r) => r.data?.response || []).catch(() => []),
      fetchGlobalFilterAssetCategories().then((r) => r.data?.response || []).catch(() => []),
      fetchGlobalFilterContracts().then((r) => r.data?.response || []).catch(() => []),
      fetchGlobalFilterAssets().then((r) => r.data?.response || []).catch(() => []),
      fetchGlobalFilterWorkOrderCategories().then((r) => r.data?.response || []).catch(() => []),
    ]).then(([techs, locs, crits, vendors, types, contracts, assets, woCategories]) => {
      if (cancelled) return;
      setTechnicianOptions(techs);
      setLocationOptions(locs);
      setCriticalityOptions(crits);
      setVendorOptions(vendors);
      setAssetTypeOptions(types);
      setContractOptions(contracts);
      setAssetOptions(assets);
      setWorkOrderCategoryOptions(woCategories);
    }).finally(() => {
      if (!cancelled) setRefDataLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  // The active tab's filter key for backend wiring. Overview reuses the
  // WO filter set because it largely surfaces WO-side metrics.
  // Reporting Details (tab "4") shares the Work Orders filter set so the
  // data underneath is the same as what the WO-tab fetches produced — a
  // user filtering Vendor=ACME on tab 2 and switching to tab 4 sees the
  // same filtered numbers in the full-size detail view.
  const activeFilterKey = activeTab === '3'
    ? 'assetHealth'
    : (activeTab === '2' || activeTab === '4') ? 'workOrders' : 'overview';
  const activeFilters = tabFilters[activeFilterKey] || emptyFilters;

  const activeContext = useSelector((s) => s.ActiveContext);
  const switching = !!activeContext?.switching;
  // Under the new model the page is reachable from any context (global or org)
  // — the permission gate is what protects access. Always load when mounted.
  const aggregateOrgCount = activeContext?.aggregateOrgCount || 0;
  const totalMembershipCount = Array.isArray(activeContext?.memberships)
    ? activeContext.memberships.length
    : 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Pass the sub-org filter to every data endpoint. The backend
      // intersects with the caller's permitted set, so a client-side
      // bypass can never widen scope. Empty/undefined = no filter.
      const filterIds = selectedOrgIds.length > 0 ? selectedOrgIds : undefined;
      // WO-side widgets use the active-tab filter values (the WO filter
      // chips are on Overview + Work Orders tabs).
      const window = {
        from: dateRangeIso.from,
        to: dateRangeIso.to,
        subOrgIds: filterIds,
        filters: activeFilters,
      };
      // Prior window — equal length, immediately preceding the current
      // selection. Drives the delta arrows on the Overview cards.
      const rangeMs = dateRange.endDate.getTime() - dateRange.startDate.getTime();
      const priorStart = new Date(dateRange.startDate.getTime() - rangeMs);
      const priorEnd = new Date(dateRange.startDate.getTime());
      const prevWindow = {
        from: priorStart.toISOString().slice(0, 10),
        to: priorEnd.toISOString().slice(0, 10),
        subOrgIds: filterIds,
        filters: activeFilters,
      };
      const [
        k, tr, o, orgs, perf, breakdown, aging,
        mostProb, dtBreakdown, totalDt, dtCost, mtbf,
        status, onTime, ttc, curStatus, repeating, cbu,
        prevPerf, prevTr, prevBreakdown,
      ] = await Promise.all([
        fetchGlobalDashboardKpis({ subOrgIds: filterIds }),
        fetchGlobalWoTrend(window),
        fetchGlobalTopOverdue({ limit: 10, subOrgIds: filterIds }),
        fetchGlobalSubOrganizations(),
        fetchGlobalPerformance(window),
        fetchGlobalWoBreakdown(window),
        fetchGlobalAgingBuckets({ subOrgIds: filterIds, filters: activeFilters }),
        // Asset Health widgets take the Asset-tab filter set (criticality /
        // assetType / location / vendor / etc). Even when the user is on
        // a different tab we pass the Asset Health filters because those
        // widgets only render on the Assets tab anyway — making them
        // respond to the live filter selection without per-tab guards.
        fetchGlobalMostProblematic({ ...window, limit: 10, filters: tabFilters.assetHealth }),
        fetchGlobalDowntimeBreakdown({ ...window, limit: 50, filters: tabFilters.assetHealth }),
        fetchGlobalTotalDowntime({ ...window, filters: tabFilters.assetHealth }),
        fetchGlobalDowntimeCost({ ...window, filters: tabFilters.assetHealth }),
        fetchGlobalAssetMtbf({ ...window, limit: 50, filters: tabFilters.assetHealth }),
        fetchGlobalStatusReport({ ...window, filters: activeFilters }),
        fetchGlobalOnTimeOverdue({ ...window, filters: activeFilters }),
        fetchGlobalTimeToComplete({ ...window, filters: activeFilters }),
        fetchGlobalCurrentStatus({ subOrgIds: filterIds, filters: tabFilters.assetHealth }),
        fetchGlobalRepeatingReport({ ...window, filters: activeFilters }),
        fetchGlobalCompletedByUser({ ...window, filters: activeFilters }),
        // Prior-window aggregates — Overview deltas only.
        fetchGlobalPerformance(prevWindow).catch(() => ({ data: { response: null } })),
        fetchGlobalWoTrend(prevWindow).catch(() => ({ data: { response: null } })),
        fetchGlobalWoBreakdown(prevWindow).catch(() => ({ data: { response: null } })),
      ]);
      setKpis(k.data?.response ?? null);
      setTrend(tr.data?.response ?? null);
      setTopOverdue(Array.isArray(o.data?.response) ? o.data.response : []);
      setPerformance(perf.data?.response ?? null);
      setWoBreakdown(breakdown.data?.response ?? null);
      setAgingBuckets(Array.isArray(aging.data?.response?.perSubOrg) ? aging.data.response.perSubOrg : []);
      setMostProblematic(mostProb.data?.response ?? { totalAssets: 0, assets: [] });
      setDowntimeBreakdown(dtBreakdown.data?.response ?? { totalAssets: 0, assets: [] });
      setTotalDowntime(totalDt.data?.response ?? null);
      setDowntimeCost(dtCost.data?.response ?? null);
      setAssetMtbf(mtbf.data?.response ?? null);
      setStatusReport(status.data?.response ?? null);
      setOnTimeOverdue(onTime.data?.response ?? null);
      setTimeToComplete(ttc.data?.response ?? null);
      setCurrentStatus(curStatus.data?.response ?? null);
      setRepeatingReport(repeating.data?.response ?? null);
      setCompletedByUser(cbu.data?.response ?? null);
      setPrevPerformance(prevPerf.data?.response ?? null);
      setPrevTrend(prevTr.data?.response ?? null);
      setPrevWoBreakdown(prevBreakdown.data?.response ?? null);
      const liveSubOrgs = Array.isArray(orgs.data?.response) ? orgs.data.response : [];
      setSubOrgs(liveSubOrgs);
      // Reconcile sidebar's hasAggregateView flags with the live permission set
      // — fixes the stale "(N orgs)" count after a role change.
      dispatch(refreshAggregateViewOrgs(liveSubOrgs.map((sub) => sub.id)));
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load reporting data');
    } finally {
      setLoading(false);
    }
  }, [dispatch, dateRangeIso.from, dateRangeIso.to, selectedOrgIds, activeFilters, tabFilters.assetHealth]);

  useEffect(() => {
    load();
  }, [load]);

  // Click on a sub-org row's name — open the confirmation modal first.
  // Actual dispatch happens in confirmJumpIntoSubOrg.
  const requestJumpIntoSubOrg = (subOrg) => {
    if (switching) return;
    setPendingSwitch({ organizationId: subOrg.subOrgId, name: subOrg.subOrgName });
  };

  const cancelJumpIntoSubOrg = () => {
    if (switching) return;
    setPendingSwitch(null);
  };

  const confirmJumpIntoSubOrg = () => {
    if (!pendingSwitch) return;
    dispatch(
      switchContext(pendingSwitch.organizationId, () => {
        setPendingSwitch(null);
        window.location.href = '/reactive';
      })
    );
  };

  // =========================================================================
  // Drill-down click handlers (Phase 3)
  //
  // Every WO-tab widget passes click events into one of the handlers below.
  // The shared {@code runDrillFetch} helper runs the fetcher, plumbs the
  // {workOrders, total, type, periodLabel} payload into local state, and
  // pops the WorkOrderListModal. The Organization chip's current selection
  // is threaded as {@code subOrgIds} so the drill mirrors the chart slice
  // (per Phase 0 decision: "Honor the Organization chip filter").
  // =========================================================================
  const drillSubOrgIds = useMemo(
    () => (selectedOrgIds.length > 0 ? selectedOrgIds : undefined),
    [selectedOrgIds]
  );

  const runDrillFetch = useCallback(async (fetcher, params) => {
    setWorkOrderListLoading(true);
    setWorkOrderListError(null);
    setWorkOrderList(null);
    setModalOpen(true);
    try {
      const res = await fetcher({ subOrgIds: drillSubOrgIds, ...params });
      setWorkOrderList(res?.data?.response ?? null);
    } catch (e) {
      setWorkOrderListError(e?.response?.data?.message || e?.message || 'Failed to load work orders');
    } finally {
      setWorkOrderListLoading(false);
    }
  }, [drillSubOrgIds]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setWorkOrderList(null);
    setWorkOrderListError(null);
  }, []);

  // Reporting Details routing — every WO-tab widget's title-chevron passes
  // its report key here. We preselect it and jump to tab "4" so the user
  // lands on the full-size widget directly. Mirrors per-org behavior.
  const handleViewReportDetails = useCallback((reportKey) => {
    setDetailReportKey(reportKey);
    setActiveTab('4');
  }, []);

  // --- Created vs Resolved ---
  const handleCreatedClick = useCallback(() => {
    runDrillFetch(fetchGlobalCreatedList, {
      from: dateRangeIso.from,
      to: dateRangeIso.to,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handleResolvedClick = useCallback(() => {
    runDrillFetch(fetchGlobalResolvedList, {
      from: dateRangeIso.from,
      to: dateRangeIso.to,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handleChartPointClick = useCallback((payload, type) => {
    if (!payload) return;
    const fetcher = type === 'CREATED' ? fetchGlobalCreatedList : fetchGlobalResolvedList;
    runDrillFetch(fetcher, {
      from: dateRangeIso.from,
      to: dateRangeIso.to,
      periodStart: formatDateTimeForAPI(payload.periodStart),
      periodEnd: formatDateTimeForAPI(payload.periodEnd),
      periodLabel: payload.periodLabel,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  // --- WO By Type ---
  const handlePreventiveClick = useCallback(() => {
    runDrillFetch(fetchGlobalByTypeList, {
      from: dateRangeIso.from, to: dateRangeIso.to, workType: 'Preventive',
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handleReactiveClick = useCallback(() => {
    runDrillFetch(fetchGlobalByTypeList, {
      from: dateRangeIso.from, to: dateRangeIso.to, workType: 'Reactive',
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handleOtherClick = useCallback(() => {
    runDrillFetch(fetchGlobalByTypeList, {
      from: dateRangeIso.from, to: dateRangeIso.to, workType: 'Other',
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handleWorkTypeChartBarClick = useCallback((payload, workType) => {
    if (!payload) return;
    runDrillFetch(fetchGlobalByTypeList, {
      from: dateRangeIso.from,
      to: dateRangeIso.to,
      workType,
      periodStart: formatDateTimeForAPI(payload.periodStart),
      periodEnd: formatDateTimeForAPI(payload.periodEnd),
      periodLabel: payload.periodLabel,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  // --- Repeating ---
  const handleNonRepeatingClick = useCallback(() => {
    runDrillFetch(fetchGlobalRepeatingList, {
      from: dateRangeIso.from, to: dateRangeIso.to, isRepeating: false,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handleRepeatingClick = useCallback(() => {
    runDrillFetch(fetchGlobalRepeatingList, {
      from: dateRangeIso.from, to: dateRangeIso.to, isRepeating: true,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handleRepeatingChartBarClick = useCallback((payload, isRepeating) => {
    if (!payload) return;
    runDrillFetch(fetchGlobalRepeatingList, {
      from: dateRangeIso.from,
      to: dateRangeIso.to,
      isRepeating,
      periodStart: formatDateTimeForAPI(payload.periodStart),
      periodEnd: formatDateTimeForAPI(payload.periodEnd),
      periodLabel: payload.periodLabel,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  // --- Status / Priority ---
  const handleStatusClick = useCallback((status) => {
    runDrillFetch(fetchGlobalStatusList, {
      from: dateRangeIso.from, to: dateRangeIso.to, status,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handlePriorityClick = useCallback((priority) => {
    runDrillFetch(fetchGlobalPriorityList, {
      from: dateRangeIso.from, to: dateRangeIso.to, priority,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  // --- Time to Complete ---
  const handleTotalHoursClick = useCallback(() => {
    runDrillFetch(fetchGlobalTimeToCompleteList, {
      from: dateRangeIso.from, to: dateRangeIso.to,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handleMttrClick = useCallback(() => {
    runDrillFetch(fetchGlobalMttrList, {
      from: dateRangeIso.from, to: dateRangeIso.to,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handleTimeToCompleteChartClick = useCallback((payload, type) => {
    if (!payload) return;
    const fetcher = type === 'MTTR' ? fetchGlobalMttrList : fetchGlobalTimeToCompleteList;
    runDrillFetch(fetcher, {
      from: dateRangeIso.from,
      to: dateRangeIso.to,
      periodStart: formatDateTimeForAPI(payload.periodStart),
      periodEnd: formatDateTimeForAPI(payload.periodEnd),
      periodLabel: payload.periodLabel,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  // --- On-Time vs Overdue ---
  const handleOnTimeClick = useCallback(() => {
    runDrillFetch(fetchGlobalOnTimeOverdueList, {
      from: dateRangeIso.from, to: dateRangeIso.to, overdueStatus: 'ON_TIME',
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handleOverdueClick = useCallback(() => {
    runDrillFetch(fetchGlobalOnTimeOverdueList, {
      from: dateRangeIso.from, to: dateRangeIso.to, overdueStatus: 'OVERDUE',
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  const handleOnTimeOverdueSegmentClick = useCallback((segmentName, isRepeating) => {
    runDrillFetch(fetchGlobalOnTimeOverdueList, {
      from: dateRangeIso.from,
      to: dateRangeIso.to,
      overdueStatus: segmentName,
      isRepeating,
    });
  }, [runDrillFetch, dateRangeIso.from, dateRangeIso.to]);

  // --- Completed by User ---
  const handleUserWeekClick = useCallback((userId, periodIndex) => {
    const period = completedByUser?.periods?.[periodIndex];
    if (!period) return;
    runDrillFetch(fetchGlobalCompletedByUserList, {
      userId,
      weekStart: formatDateTimeForAPI(period.start),
      weekEnd: formatDateTimeForAPI(period.end),
    });
  }, [runDrillFetch, completedByUser]);

  // Server-side filtering: every /global/* endpoint now intersects the
  // requested subOrgIds with the caller's permitted set (with a 403 on
  // any unpermitted ID), so responses are already narrowed. The
  // chart/table sources below read the server payload directly — the
  // previous client-side re-filter memos are gone.
  // The server returns a weighted-correct {@code enterprise} block for
  // both the unfiltered and filtered cases; no FE recompute needed.
  const enterprisePerformance = performance?.enterprise || null;

  // =========================================================================
  // Per-org Reporting widget adapters. Each shapes a global API response to
  // match the {data: {summary, chartData}} contract the per-org widgets expect
  // so we can embed those components directly and inherit their visual style
  // (per CLAUDE.md rule #9 — Work Orders + Assets reporting is the canonical
  // design pattern; we don't invent parallel widgets for the global page).
  // =========================================================================
  const createdVsResolvedData = useMemo(() => {
    if (!trend) return null;
    const days = trend.days || [];
    const chartData = days.map((d) => ({
      periodLabel: d.date ? d.date.slice(5) : '',  // 'MM-DD' to match per-org chart density
      periodStart: d.date,
      createdCount: d.created || 0,
      resolvedCount: d.resolved || 0,
    }));
    const createdCount = chartData.reduce((a, d) => a + d.createdCount, 0);
    const resolvedCount = chartData.reduce((a, d) => a + d.resolvedCount, 0);
    const resolutionPercentage = createdCount === 0
      ? 0
      : (resolvedCount / createdCount) * 100;
    return { summary: { createdCount, resolvedCount, resolutionPercentage }, chartData };
  }, [trend]);

  // WO by Type: consumes the backend's `dailySeries` (already merged
  // across selected sub-orgs and bucketed per period), so the chart's
  // x-axis is time — matching the per-org widget's contract verbatim.
  const wosByTypeData = useMemo(() => {
    if (!woBreakdown) return null;
    const series = Array.isArray(woBreakdown.dailySeries) ? woBreakdown.dailySeries : [];
    const chartData = series.map((d) => ({
      periodLabel: d.periodLabel,
      periodStart: d.periodStart,
      preventiveCount: d.preventiveCount || 0,
      reactiveCount: d.reactiveCount || 0,
      otherCount: d.otherCount || 0,
    }));
    const preventiveCount = chartData.reduce((a, d) => a + d.preventiveCount, 0);
    const reactiveCount = chartData.reduce((a, d) => a + d.reactiveCount, 0);
    const otherCount = chartData.reduce((a, d) => a + d.otherCount, 0);
    const total = preventiveCount + reactiveCount + otherCount;
    const preventivePercentage = total === 0 ? 0 : (preventiveCount / total) * 100;
    return {
      summary: { preventiveCount, reactiveCount, otherCount, preventivePercentage },
      chartData,
    };
  }, [woBreakdown]);

  // Priority: collapse Critical+High into highCount (per-org widget doesn't
  // have a Critical bucket); Other → noneCount.
  const priorityData = useMemo(() => {
    if (!woBreakdown) return null;
    const rows = woBreakdown.perSubOrg || [];
    const highCount = sum(rows, (r) => (r.critical || 0) + (r.high || 0));
    const mediumCount = sum(rows, (r) => r.medium);
    const lowCount = sum(rows, (r) => r.low);
    const noneCount = sum(rows, (r) => r.otherPriority);
    const total = highCount + mediumCount + lowCount + noneCount;
    return { summary: { noneCount, lowCount, mediumCount, highCount, total } };
  }, [woBreakdown]);

  const toggleOrgSelection = (orgId) => {
    setSelectedOrgIds((prev) =>
      prev.includes(orgId) ? prev.filter((x) => x !== orgId) : [...prev, orgId]
    );
  };

  // Adapter for DateRangeSelector's onChange contract — it passes ISO strings,
  // we keep Date objects in state to share the per-org contract exactly.
  const handleDateRangeChange = (startISO, endISO) => {
    setDateRange({ startDate: new Date(startISO), endDate: new Date(endISO) });
  };

  // =========================================================================
  // Overview tab adapters — shape existing payloads into the new layout:
  // aggregate %, prior-period delta, and per-org rows. All client-side.
  // =========================================================================
  const overviewAssetHealth = useMemo(() => {
    const onTime = enterprisePerformance?.onTimeCount || 0;
    const late = enterprisePerformance?.lateCount || 0;
    const onTimePct = safePct(onTime, onTime + late);
    const prevOnTime = prevPerformance?.enterprise?.onTimeCount || 0;
    const prevLate = prevPerformance?.enterprise?.lateCount || 0;
    const prevOnTimePct = safePct(prevOnTime, prevOnTime + prevLate);

    return {
      onTimePct,
      onTimeDelta: percentDelta(onTimePct, prevOnTimePct),
      mtbfHours: enterprisePerformance?.mtbfHours ?? null,
      mtbfDelta: percentDelta(
        enterprisePerformance?.mtbfHours,
        prevPerformance?.enterprise?.mtbfHours
      ),
      mttrHours: enterprisePerformance?.mttrHours ?? null,
      mttrDelta: percentDelta(
        enterprisePerformance?.mttrHours,
        prevPerformance?.enterprise?.mttrHours
      ),
    };
  }, [enterprisePerformance, prevPerformance]);

  const overviewCreatedVsCompleted = useMemo(() => {
    const created = enterprisePerformance?.createdInWindow ?? 0;
    const completed = enterprisePerformance?.completedInWindow ?? 0;
    const aggregatePct = safePct(completed, created);
    const prevCreated = prevPerformance?.enterprise?.createdInWindow ?? 0;
    const prevCompleted = prevPerformance?.enterprise?.completedInWindow ?? 0;
    const prevAggregatePct = safePct(prevCompleted, prevCreated);

    const orgs = (performance?.perSubOrg || []).map((row) => {
      const cur = safePct(row.completedInWindow ?? 0, row.createdInWindow ?? 0);
      const prevRow = (prevPerformance?.perSubOrg || []).find(
        (p) => p.subOrgId === row.subOrgId
      );
      const prev = prevRow
        ? safePct(prevRow.completedInWindow ?? 0, prevRow.createdInWindow ?? 0)
        : null;
      let trend = null;
      if (cur != null && prev != null && Math.abs(cur - prev) >= 0.5) {
        trend = cur > prev ? 'up' : 'down';
      }
      return {
        subOrgId: row.subOrgId,
        subOrgName: row.subOrgName,
        pct: cur,
        trend,
      };
    });

    return {
      aggregatePct,
      aggregateDelta: percentDelta(aggregatePct, prevAggregatePct),
      orgs,
    };
  }, [enterprisePerformance, prevPerformance, performance]);

  const overviewOnTimeOverdue = useMemo(() => {
    const onTime = enterprisePerformance?.onTimeCount || 0;
    const late = enterprisePerformance?.lateCount || 0;
    const overdueOpen = enterprisePerformance?.openOverdueCount || 0;
    const total = onTime + late + overdueOpen;
    const aggregatePct = safePct(onTime, total);
    const prevOnTime = prevPerformance?.enterprise?.onTimeCount || 0;
    const prevLate = prevPerformance?.enterprise?.lateCount || 0;
    const prevOverdue = prevPerformance?.enterprise?.openOverdueCount || 0;
    const prevTotal = prevOnTime + prevLate + prevOverdue;
    const prevAggregatePct = safePct(prevOnTime, prevTotal);

    const orgs = (performance?.perSubOrg || []).map((row) => ({
      subOrgId: row.subOrgId,
      subOrgName: row.subOrgName,
      segments: [
        { value: row.onTimeCount || 0, color: ROW_COLORS.onTime, label: 'On time' },
        { value: row.lateCount || 0, color: ROW_COLORS.late, label: 'Late' },
        { value: row.openOverdueCount || 0, color: ROW_COLORS.overdue, label: 'Open overdue' },
      ],
    }));

    return {
      aggregatePct,
      aggregateDelta: percentDelta(aggregatePct, prevAggregatePct),
      orgs,
    };
  }, [enterprisePerformance, prevPerformance, performance]);

  const overviewPreventiveReactive = useMemo(() => {
    const rows = woBreakdown?.perSubOrg || [];
    const totalPrev = rows.reduce((a, r) => a + (r.preventive || 0), 0);
    const totalReact = rows.reduce(
      (a, r) => a + (r.reactive || 0) + (r.corrective || 0) + (r.otherType || 0),
      0
    );
    const aggregatePct = safePct(totalPrev, totalPrev + totalReact);

    const prevRows = prevWoBreakdown?.perSubOrg || [];
    const prevTotalPrev = prevRows.reduce((a, r) => a + (r.preventive || 0), 0);
    const prevTotalReact = prevRows.reduce(
      (a, r) => a + (r.reactive || 0) + (r.corrective || 0) + (r.otherType || 0),
      0
    );
    const prevAggregatePct = safePct(prevTotalPrev, prevTotalPrev + prevTotalReact);

    const orgs = rows.map((r) => ({
      subOrgId: r.subOrgId,
      subOrgName: r.subOrgName,
      segments: [
        { value: r.preventive || 0, color: ROW_COLORS.preventive, label: 'Preventive' },
        {
          value: (r.reactive || 0) + (r.corrective || 0) + (r.otherType || 0),
          color: ROW_COLORS.reactive,
          label: 'Reactive',
        },
      ],
    }));

    return {
      aggregatePct,
      aggregateDelta: percentDelta(aggregatePct, prevAggregatePct),
      orgs,
    };
  }, [woBreakdown, prevWoBreakdown]);

  const overviewOrgRows = useMemo(() => {
    const perfMap = new Map(
      (performance?.perSubOrg || []).map((r) => [r.subOrgId, r])
    );
    const breakdownMap = new Map(
      (woBreakdown?.perSubOrg || []).map((r) => [r.subOrgId, r])
    );
    const ids = new Set([
      ...(performance?.perSubOrg || []).map((r) => r.subOrgId),
      ...(woBreakdown?.perSubOrg || []).map((r) => r.subOrgId),
    ]);

    return Array.from(ids).map((id) => {
      const p = perfMap.get(id);
      const b = breakdownMap.get(id);
      const completedPct = p ? safePct(p.completedInWindow ?? 0, p.createdInWindow ?? 0) : null;
      const onTimePct = p
        ? safePct(p.onTimeCount || 0, (p.onTimeCount || 0) + (p.lateCount || 0))
        : null;
      const reactiveTotal = b
        ? (b.reactive || 0) + (b.corrective || 0) + (b.otherType || 0)
        : 0;
      const preventivePct = b
        ? safePct(b.preventive || 0, (b.preventive || 0) + reactiveTotal)
        : null;
      return {
        subOrgId: id,
        subOrgName: p?.subOrgName || b?.subOrgName || '',
        completedPct,
        onTimePct,
        preventivePct,
      };
    });
  }, [performance, woBreakdown]);

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Page header — mirrors ReportingDashboard.jsx: title on the left,
            right-aligned DateRangeSelector chip + outline Refresh button. */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4"
        >
          <h4 className="mb-0 fw-bold">{t('shared.reporting', 'Reporting')}</h4>

          <div className="d-flex align-items-center gap-3 flex-wrap">
            <DateRangeSelector
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={handleDateRangeChange}
            />
            <motion.button
              className="btn btn-outline-primary d-flex align-items-center gap-2"
              onClick={load}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw
                size={18}
                style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}
              />
              {t('shared.refresh', 'Refresh')}
            </motion.button>
          </div>
        </motion.div>

        {aggregateOrgCount > 0 && (
          <Alert color="info" className="mb-3">
            {t(
              'shared.global_view.scope_label',
              'Aggregated across {{count}} of your {{total}} organizations',
              { count: aggregateOrgCount, total: totalMembershipCount }
            )}
          </Alert>
        )}

        {error && <Alert color="danger">{error}</Alert>}

        {loading && !kpis && (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        )}

        {kpis && (
          <>
            {/* Three-tab layout: Overview as the landing summary, then
                the WO and Assets drill-downs. Tab ids are 1/2/3 — bumped
                from the previous 1/2 (WO/Assets) layout. */}
            <Nav tabs className="reporting-tabs mb-4 border-bottom">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === '1' })}
                  onClick={() => setActiveTab('1')}
                  style={{ cursor: 'pointer' }}
                >
                  <LayoutDashboard size={16} className="me-2" />
                  {t('shared.global_org.tab_overview', 'Overview')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === '2' })}
                  onClick={() => setActiveTab('2')}
                  style={{ cursor: 'pointer' }}
                >
                  <ClipboardList size={16} className="me-2" />
                  {t('shared.work_orders', 'Work Orders')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === '3' })}
                  onClick={() => setActiveTab('3')}
                  style={{ cursor: 'pointer' }}
                >
                  <Activity size={16} className="me-2" />
                  {t('shared.asset_health', 'Asset Health')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === '4' })}
                  onClick={() => setActiveTab('4')}
                  style={{ cursor: 'pointer' }}
                >
                  <FileText size={16} className="me-2" />
                  {t('shared.tab_reporting_details', 'Reporting Details')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === '5' })}
                  onClick={() => setActiveTab('5')}
                  style={{ cursor: 'pointer' }}
                >
                  <Download size={16} className="me-2" />
                  {t('shared.global_org.tab_export_data', 'Export Data')}
                </NavLink>
              </NavItem>
            </Nav>

            {/* Per-tab filter bar — mirrors the per-org Reporting bar so
                users get the same Assigned To / Due Date / Location /
                Priority chips + Add Filter pool. Filter values feed into
                the data fetchers below. */}
            {activeTab === '1' && (
              <ReportingFilterBar
                tabKey="workOrders"
                filters={tabFilters.overview}
                onChange={(p) => updateTabFilters('overview', p)}
                visibleExtras={visibleExtras.overview}
                onAddFilter={(k) => addExtraFilter('overview', k)}
                onRemoveFilter={(k) => removeExtraFilter('overview', k)}
                technicianOptions={technicianOptions}
                locationOptions={locationOptions}
                vendorOptions={vendorOptions}
                assetTypeOptions={assetTypeOptions}
                assetOptions={assetOptions}
                criticalityOptions={criticalityOptions}
                priorityOptions={PRIORITY_OPTIONS}
                statusOptions={STATUS_OPTIONS}
                workOrderCategoryOptions={workOrderCategoryOptions}
                contractOptions={contractOptions}
                refDataLoading={refDataLoading}
                organizationOptions={subOrgs}
                selectedOrganizationIds={selectedOrgIds}
                onToggleOrganization={toggleOrgSelection}
                onClearOrganizations={() => setSelectedOrgIds([])}
              />
            )}
            {(activeTab === '2' || activeTab === '4') && (
              <ReportingFilterBar
                tabKey="workOrders"
                filters={tabFilters.workOrders}
                onChange={(p) => updateTabFilters('workOrders', p)}
                visibleExtras={visibleExtras.workOrders}
                onAddFilter={(k) => addExtraFilter('workOrders', k)}
                onRemoveFilter={(k) => removeExtraFilter('workOrders', k)}
                technicianOptions={technicianOptions}
                locationOptions={locationOptions}
                vendorOptions={vendorOptions}
                assetTypeOptions={assetTypeOptions}
                assetOptions={assetOptions}
                criticalityOptions={criticalityOptions}
                priorityOptions={PRIORITY_OPTIONS}
                statusOptions={STATUS_OPTIONS}
                workOrderCategoryOptions={workOrderCategoryOptions}
                contractOptions={contractOptions}
                refDataLoading={refDataLoading}
                organizationOptions={subOrgs}
                selectedOrganizationIds={selectedOrgIds}
                onToggleOrganization={toggleOrgSelection}
                onClearOrganizations={() => setSelectedOrgIds([])}
              />
            )}
            {activeTab === '3' && (
              <ReportingFilterBar
                tabKey="assetHealth"
                filters={tabFilters.assetHealth}
                onChange={(p) => updateTabFilters('assetHealth', p)}
                visibleExtras={visibleExtras.assetHealth}
                onAddFilter={(k) => addExtraFilter('assetHealth', k)}
                onRemoveFilter={(k) => removeExtraFilter('assetHealth', k)}
                locationOptions={locationOptions}
                criticalityOptions={criticalityOptions}
                vendorOptions={vendorOptions}
                assetTypeOptions={assetTypeOptions}
                contractOptions={contractOptions}
                refDataLoading={refDataLoading}
                organizationOptions={subOrgs}
                selectedOrganizationIds={selectedOrgIds}
                onToggleOrganization={toggleOrgSelection}
                onClearOrganizations={() => setSelectedOrgIds([])}
              />
            )}

            <TabContent activeTab={activeTab}>
              {/* ============ TAB: OVERVIEW (landing) ============
                  Enterprise summary across WO + Asset health. The two
                  drill-down tabs (Work Orders, Assets) hold the specialty
                  charts; this tab is what an exec scans first. */}
              <TabPane tabId="1">
                <SectionHeading>
                  {t('shared.global_org.overview.section_asset_health', 'Asset Health')}
                </SectionHeading>
                <Row className="g-3 mb-3">
                  <Col xs={12}>
                    <AssetStatusCard data={currentStatus} />
                  </Col>
                </Row>
                <Row className="g-3 mb-4">
                  <Col xs={12} lg={6}>
                    <AvailabilityCard data={currentStatus} />
                  </Col>
                  <Col xs={12} lg={6}>
                    <MtbfMttrCard
                      mtbfHours={overviewAssetHealth.mtbfHours}
                      mttrHours={overviewAssetHealth.mttrHours}
                      mtbfDelta={overviewAssetHealth.mtbfDelta}
                      mttrDelta={overviewAssetHealth.mttrDelta}
                    />
                  </Col>
                </Row>

                <SectionHeading>
                  {t('shared.global_org.overview.section_work_orders', 'Work Orders')}
                </SectionHeading>
                <Row className="g-3 mb-4">
                  <Col xs={12} lg={4}>
                    <WoBreakdownCard
                      title={t('shared.global_org.overview.created_vs_completed',
                        'Created vs. Completed')}
                      subtitle={t('shared.global_org.overview.created_vs_completed_subtitle',
                        'Overall percent completed')}
                      aggregatePct={overviewCreatedVsCompleted.aggregatePct}
                      aggregateDelta={overviewCreatedVsCompleted.aggregateDelta}
                      betterIsUp
                      orgs={overviewCreatedVsCompleted.orgs}
                      renderRow={(org) => <CompletedPctRow org={org} />}
                      sortValue={(o) => o.pct}
                    />
                  </Col>
                  <Col xs={12} lg={4}>
                    <WoBreakdownCard
                      title={t('shared.global_org.overview.on_time_vs_overdue',
                        'On Time vs. Overdue')}
                      subtitle={t('shared.global_org.overview.on_time_vs_overdue_subtitle',
                        'Overall completed on time')}
                      aggregatePct={overviewOnTimeOverdue.aggregatePct}
                      aggregateDelta={overviewOnTimeOverdue.aggregateDelta}
                      betterIsUp
                      orgs={overviewOnTimeOverdue.orgs}
                      renderRow={(org) => <SegmentedBarRow org={org} />}
                      sortValue={(o) => {
                        const total = o.segments.reduce((a, s) => a + s.value, 0);
                        return total === 0 ? -1 : (o.segments[0].value / total) * 100;
                      }}
                      legend={[
                        { color: ROW_COLORS.onTime, label: t('shared.global_org.on_time', 'On time') },
                        { color: ROW_COLORS.late, label: t('shared.global_org.late', 'Late') },
                        { color: ROW_COLORS.overdue, label: t('shared.global_org.open_overdue', 'Open & overdue') },
                      ]}
                    />
                  </Col>
                  <Col xs={12} lg={4}>
                    <WoBreakdownCard
                      title={t('shared.global_org.overview.preventive_vs_reactive',
                        'Preventive vs. Reactive')}
                      subtitle={t('shared.global_org.overview.preventive_vs_reactive_subtitle',
                        'Overall preventive ratio')}
                      aggregatePct={overviewPreventiveReactive.aggregatePct}
                      aggregateDelta={overviewPreventiveReactive.aggregateDelta}
                      betterIsUp
                      orgs={overviewPreventiveReactive.orgs}
                      renderRow={(org) => <SegmentedBarRow org={org} />}
                      sortValue={(o) => {
                        const total = o.segments.reduce((a, s) => a + s.value, 0);
                        return total === 0 ? -1 : (o.segments[0].value / total) * 100;
                      }}
                      legend={[
                        { color: ROW_COLORS.preventive, label: t('shared.global_org.work_type_preventive', 'Preventive') },
                        { color: ROW_COLORS.reactive, label: t('shared.global_org.work_type_reactive', 'Reactive') },
                      ]}
                    />
                  </Col>
                </Row>

                <OrganizationsTable
                  rows={overviewOrgRows}
                  onOrgClick={(row) => {
                    const canJump = (activeContext?.memberships || [])
                      .some((m) => m.organizationId === row.subOrgId);
                    if (canJump) requestJumpIntoSubOrg(row);
                  }}
                />
              </TabPane>

              {/* ============ TAB: ASSETS ============
                  Embeds the per-org Asset Health widgets directly. The
                  per-org components self-fetch by default; passing an
                  explicit `data` prop puts them in external mode and
                  bypasses their internal Redux/API dispatch. Sub-org
                  context is encoded into `locationName` (Most Problematic,
                  Current Status) or prepended to `assetName` (Downtime
                  Breakdown) so the cross-org rows remain identifiable
                  without changing the per-org widget schemas. */}
              <TabPane tabId="3">
                <div className="row g-4">
                  <div className="col-12">
                    <CurrentStatusWidget data={currentStatus} loading={loading} error={error} />
                  </div>
                  <div className="col-12">
                    <MostProblematicWidget data={mostProblematic} loading={loading} error={error} />
                  </div>
                  <div className="col-12">
                    <DowntimeBreakdownWidget data={downtimeBreakdown} loading={loading} error={error} />
                  </div>
                  <div className="col-12">
                    <TotalDowntimeWidget data={totalDowntime} loading={loading} error={error} />
                  </div>
                  <div className="col-12">
                    <DowntimeCostWidget data={downtimeCost} loading={loading} error={error} />
                  </div>
                  <div className="col-12">
                    <MtbfWidget data={assetMtbf} loading={loading} error={error} />
                  </div>
                </div>
              </TabPane>

              {/* ============ TAB: WORK ORDERS ============
                  Embeds the per-org Reporting widgets directly (per CLAUDE.md
                  rule #9). Global data is reshaped into the per-org {summary,
                  chartData} contract by the adapter memos defined above.

                  Per-org widgets we still need to embed (need backend data
                  extensions, deferred to Phase 1.5):
                  - StatusReportWidget       (needs global status counts)
                  - OnTimeOverdueWidget      (needs nonRepeating/repeating split)
                  - TimeToCompleteWidget     (needs daily MTTR series)
                  Until those land, the Top-Org Rankings + per-sub-org
                  comparison fill the gap. */}
              <TabPane tabId="2">
                <div className="row g-4">
                  <div className="col-12">
                    <CreatedVsResolvedWidget
                      data={createdVsResolvedData}
                      loading={loading}
                      error={error}
                      onCreatedClick={handleCreatedClick}
                      onResolvedClick={handleResolvedClick}
                      onChartPointClick={handleChartPointClick}
                      onDetailsClick={() => handleViewReportDetails('createdVsResolved')}
                    />
                  </div>
                  <div className="col-12">
                    <WorkOrdersByTypeWidget
                      data={wosByTypeData}
                      loading={loading}
                      error={error}
                      onPreventiveClick={handlePreventiveClick}
                      onReactiveClick={handleReactiveClick}
                      onOtherClick={handleOtherClick}
                      onChartBarClick={handleWorkTypeChartBarClick}
                      onDetailsClick={() => handleViewReportDetails('workOrdersByType')}
                    />
                  </div>
                  {/* Repeating / Status / Priority / Time-to-Complete / On-Time
                      vs Overdue / Completed-by-User come from the per-org
                      reporting service via global fan-out, so they take the
                      per-org response shape verbatim. Click handlers (Phase 3)
                      drill into a global WorkOrderListModal that adds an
                      Organization column when rows carry subOrgName. */}
                  <div className="col-12">
                    <RepeatingReportWidget
                      data={repeatingReport}
                      loading={loading}
                      error={error}
                      onNonRepeatingClick={handleNonRepeatingClick}
                      onRepeatingClick={handleRepeatingClick}
                      onChartBarClick={handleRepeatingChartBarClick}
                      onDetailsClick={() => handleViewReportDetails('repeatingReport')}
                    />
                  </div>
                  <div className="col-12">
                    <StatusReportWidget
                      data={statusReport}
                      loading={loading}
                      error={error}
                      onStatusClick={handleStatusClick}
                      onDetailsClick={() => handleViewReportDetails('statusReport')}
                    />
                  </div>
                  <div className="col-12">
                    <PriorityReportWidget
                      data={priorityData}
                      loading={loading}
                      error={error}
                      onPriorityClick={handlePriorityClick}
                      onDetailsClick={() => handleViewReportDetails('priorityReport')}
                    />
                  </div>
                  <div className="col-12">
                    <TimeToCompleteWidget
                      data={timeToComplete}
                      loading={loading}
                      error={error}
                      onTotalHoursClick={handleTotalHoursClick}
                      onAvgHoursClick={handleTotalHoursClick}
                      onMttrClick={handleMttrClick}
                      onChartPointClick={handleTimeToCompleteChartClick}
                      onDetailsClick={() => handleViewReportDetails('timeToComplete')}
                    />
                  </div>
                  <div className="col-12">
                    <OnTimeOverdueWidget
                      data={onTimeOverdue}
                      loading={loading}
                      error={error}
                      onOnTimeClick={handleOnTimeClick}
                      onOverdueClick={handleOverdueClick}
                      onSegmentClick={handleOnTimeOverdueSegmentClick}
                      onDetailsClick={() => handleViewReportDetails('onTimeOverdue')}
                    />
                  </div>
                  <div className="col-12">
                    <CompletedByUserWidget
                      data={completedByUser}
                      loading={loading}
                      error={error}
                      onUserWeekClick={handleUserWeekClick}
                      onDetailsClick={() => handleViewReportDetails('completedByUser')}
                    />
                  </div>
                  {/* Top overdue assets — global-only widget. Wrapped in
                      ReportWidget so it inherits the per-org card chrome
                      (rounded 16px, motion, blue link title with chevron). */}
                  <div className="col-12">
                    <ReportWidget
                      title={t('shared.global_org.top_overdue_title', 'Top overdue assets')}
                      loading={loading}
                      error={error}
                      isEmpty={topOverdue.length === 0}
                    >
                      <Table responsive className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>{t('shared.name', 'Name')}</th>
                            <th>{t('shared.global_org.organization', 'Organization')}</th>
                            <th>{t('shared.global_org.days_overdue', 'Days overdue')}</th>
                            <th>{t('shared.issue', 'Issue')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topOverdue.map((row, idx) => (
                            <tr key={`${row.assetId}-${idx}`}>
                              <td>{idx + 1}</td>
                              <td>{row.assetName || '-'}</td>
                              <td>{row.subOrgName || '-'}</td>
                              <td>
                                <span
                                  className="badge"
                                  style={{ background: '#ef4444', color: '#fff' }}
                                >
                                  {row.daysOverdue}
                                </span>
                              </td>
                              <td className="text-muted">{row.issueTitle || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </ReportWidget>
                  </div>
                </div>
              </TabPane>

              {/* ============ TAB: REPORTING DETAILS ============
                  Full-size single-report view. Reuses the per-org
                  ReportingDetailsTab component verbatim (Phase 4 of the WO-tab
                  parity plan) — it accepts the same per-org widget data
                  contracts that the global summary tab already adapts, so no
                  additional fetches are needed. allowedReportKeys hides the
                  three Asset-side reports (status duration / total downtime /
                  MTBF) that aren't in the global WO-tab scope. */}
              <TabPane tabId="4">
                <ReportingDetailsTab
                  selectedReport={detailReportKey}
                  onReportChange={setDetailReportKey}
                  allowedReportKeys={[
                    'createdVsResolved',
                    'workOrdersByType',
                    'repeatingReport',
                    'statusReport',
                    'priorityReport',
                    'timeToComplete',
                    'onTimeOverdue',
                    'completedByUser',
                  ]}
                  reportData={{
                    createdVsResolved: createdVsResolvedData,
                    workOrdersByType: wosByTypeData,
                    repeatingReport,
                    statusReport,
                    priorityReport: priorityData,
                    timeToComplete,
                    onTimeOverdue,
                    completedByUser,
                  }}
                  reportLoading={{
                    createdVsResolved: loading,
                    workOrdersByType: loading,
                    repeatingReport: loading,
                    statusReport: loading,
                    priorityReport: loading,
                    timeToComplete: loading,
                    onTimeOverdue: loading,
                    completedByUser: loading,
                  }}
                  reportErrors={{
                    createdVsResolved: error,
                    workOrdersByType: error,
                    repeatingReport: error,
                    statusReport: error,
                    priorityReport: error,
                    timeToComplete: error,
                    onTimeOverdue: error,
                    completedByUser: error,
                  }}
                  handlers={{
                    onCreatedClick: handleCreatedClick,
                    onResolvedClick: handleResolvedClick,
                    onChartPointClick: handleChartPointClick,
                    onPreventiveClick: handlePreventiveClick,
                    onReactiveClick: handleReactiveClick,
                    onOtherClick: handleOtherClick,
                    onWorkTypeChartBarClick: handleWorkTypeChartBarClick,
                    onNonRepeatingClick: handleNonRepeatingClick,
                    onRepeatingClick: handleRepeatingClick,
                    onRepeatingChartBarClick: handleRepeatingChartBarClick,
                    onStatusClick: handleStatusClick,
                    onPriorityClick: handlePriorityClick,
                    onTotalHoursClick: handleTotalHoursClick,
                    onAvgHoursClick: handleTotalHoursClick,
                    onMttrClick: handleMttrClick,
                    onTimeToCompleteChartClick: handleTimeToCompleteChartClick,
                    onOnTimeClick: handleOnTimeClick,
                    onOverdueClick: handleOverdueClick,
                    onOnTimeOverdueSegmentClick: handleOnTimeOverdueSegmentClick,
                    onUserWeekClick: handleUserWeekClick,
                  }}
                />
              </TabPane>

              {/* ============ TAB: EXPORT DATA ============
                  Mirrors the per-org Reporting Export Data tab verbatim
                  (per CLAUDE.md rule #9). The `mode="global"` prop swaps
                  the dispatched action to `exportGlobalCsv`, hits
                  /csv-reports/global/{type}, and prepends an Organization
                  column. The 4 sub-tabs (Work Orders, Assets, Locations,
                  Requests) reuse the same per-org form components. */}
              <TabPane tabId="5">
                <ExportDataTab mode="global" />
              </TabPane>
            </TabContent>
          </>
        )}
        <SwitchContextConfirmModal
          isOpen={!!pendingSwitch}
          targetName={pendingSwitch?.name}
          switching={switching}
          onConfirm={confirmJumpIntoSubOrg}
          onCancel={cancelJumpIntoSubOrg}
        />
        <WorkOrderListModal
          isOpen={modalOpen}
          toggle={handleModalClose}
          workOrderList={workOrderList}
          loading={workOrderListLoading}
          error={workOrderListError}
        />
      </div>

      {/* Shared tabs + spin keyframes — copied verbatim from the per-org
          ReportingDashboard so the Global page renders with the same nav-pill
          styling and Refresh button spin animation. */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spin { animation: spin 1s linear infinite; }
          .reporting-tabs { gap: 0.5rem; }
          .reporting-tabs .nav-link {
            color: #6c757d;
            background-color: #fff;
          }
          .reporting-tabs .nav-link.active {
            color: #3980c0;
            background-color: #fff;
            border-bottom: 2px solid #3980c0;
          }
        `}
      </style>
    </div>
  );
};

export default GlobalDashboard;
